import React, {useEffect, useState} from "react";

import Badge from "../../../components/ui/Badge";
import {useFilterQuery, fetchSelectData, handleFilterList} from "../../../utils";
import {UserActions, DealDasActions, OfficeActions, SeniorityActions} from "../../../actions";
import {SelectInput} from "../../../components/ui/Table";
import { UserState } from "../../../reducers/UserReducer";
import { useSelector } from "react-redux";
import { RootState } from "../../../reducers/Store";

type StaffingLeftTableConfigProps = {
    handleFilterStaffingChange: (item: string, tableType: string | null) => void
}

export const staffingLeftTableConfig = ({handleFilterStaffingChange}: StaffingLeftTableConfigProps) => {
    const [ressources, setRessources] = useState<{ options: any[]; hasNextPage: boolean } | null>(null);
    const [dealDas, setDealDas] = useState<{ options: any[]; hasNextPage: boolean } | null>(null);
    const [offices, setOffices] = useState<{ options: any[]; hasNextPage: boolean } | null>(null);
    const [seniority, setSeniority] = useState<{ options: any[]; hasNextPage: boolean } | null>(null);
    const [filterQuery, setFilterQuery] = useState<{
        ressource: string;
        das: string;
    }>({
        ressource: '',
        das: ''
    });

    const userData = useSelector((state: RootState) => state.UserReducer.userProfileData);

    const profiles = [
        {
            id: 1,
            name: 'Dirigeant',
            value: 'PA'
        },
        {
            id: 2,
            name: 'Manager',
            value: 'MA'
        },
        {
            id: 3,
            name: 'Off. Man.',
            value: 'OM'
        },
        {
            id: 4,
            name: 'Consultant',
            value: 'CO'
        }
    ];

    const handleFilter = (value: string, name: string) => {
        handleFilterList(value, name, filterQuery, setFilterQuery);
    };

    useFilterQuery(filterQuery, handleFilterStaffingChange);

    // ↓ get data for select input
    const handleGetUsers = async (search: string, page = 1) => {
        await fetchSelectData({
            action: UserActions.getAllSortedUsers,
            search:`&search=${search}`,
            page: page || 1,
            setter: setRessources,
            userData: userData as UserState['userProfileData']
        });
    }

    const handleGetDealDas = async (search: string, page = 1) => {
        // Fonction de filtrage : si l'utilisateur est MA, ne montrer que ses DAS
        const filterOrFind = userData?.role === 'MA'
            ? (das: any) => das.manager?.id === userData?.userId
            : undefined; // Pas de filtre si l'utilisateur n'est pas MA

        await fetchSelectData({
            action: DealDasActions.getAllDealDas,
            search:`&search=${search}`,
            page: page || 1,
            setter: setDealDas,
            userData: userData as UserState['userProfileData'],
            filterOrFind: filterOrFind
        });
    };

    const handleGetOffice = async (search: string, page = 1) => {
        await fetchSelectData({
            action: OfficeActions.getAllOffices,
            search:`&search=${search}`,
            page: page || 1,
            setter: setOffices,
            userData: userData as UserState['userProfileData']
        });
    }

    const handleGetSeniority = async (search: string, page = 1) => {
        await fetchSelectData({
            action: SeniorityActions.getAllSeniority,
            search:`&search=${search}`,
            page: page || 1,
            setter: setSeniority,
            userData: userData as UserState['userProfileData']
        });
    }

    useEffect(() => {
        // ↓ deal das dropdown values
        (async () => {
            await handleGetDealDas('', 1);
        })();

        // ↓ users dropdown values
        (async () => {
            await handleGetUsers('', 1);
        })();

        // ↓ offices dropdown values
        (async () => {
            await handleGetOffice('', 1);
        })();

        // ↓ seniority dropdown values
        (async () => {
            await handleGetSeniority('', 1);
        })();
    }, []);

    const getProfileName = (item: any) => {
        const profile = profiles.find(el => el.value === item.ressource.user_class);

        return profile?.name || '-';
    }

    return (
        {
            dropdown: false,
            dropdownLeftPadding: false,
            greyBackground: false,
            subline: true,
            columnConfig: [
                {
                    label: 'Ressource',
                    value: 'ressource',
                    width: 'w-[7%]',
                    sort: true,
                    component: (item: any) =>
                        (<div className="flex flex-row items-center justify-center w-full">
                            <Badge content={[item.ressource]} type="rounded"/>
                        </div>
                        ),
                    filterComponent: () => <SelectInput
                        name="ressource"
                        options={ressources?.options || []}
                        hasNextPage={ressources?.hasNextPage}
                        isSearchable={true}
                        onChange={handleFilter}
                        fetchFunction={(handleGetUsers)}
                        labelKeys={['first_name', 'last_name']}
                        valueKey={'username'}
                    />,
                    sumDisplayed: false,
                    sumBy: '',
                    sumLabel: ''
                },
                {
                    label: 'DAS',
                    value: 'das',
                    width: 'w-[18%]',
                    sort: true,
                    component: (item: any) => (
                        <div className="flex flex-row items-center justify-center">
                            <Badge content={item.das} type="rounded" color="linear-gradient(135deg, #DC3832 0%, #E36D38 46.35%, #F0A841 100%)"/>
                        </div>
                    ),
                    filterComponent: () => <SelectInput
                        name="das"
                        options={dealDas?.options?.sort((a, b) => a.name.localeCompare(b.name)) || []}
                        hasNextPage={dealDas?.hasNextPage}
                        isSearchable={true}
                        onChange={handleFilter}
                        fetchFunction={(handleGetDealDas)}
                        labelKeys={['name']}
                        valueKey={'name'}
                    />,
                    sumDisplayed: false,
                    sumBy: '',
                    sumLabel: ''
                },
                {
                    label: 'Terre d\'acceuil',
                    value: 'subsidiary',
                    width: 'w-[13%]',
                    sort: true,
                    component: (item: any) => (
                        <div className="flex flex-row items-center justify-center">
                            <Badge content={item.office}/>
                        </div>
                    ),
                    filterComponent: () => <SelectInput
                        name="office"
                        options={offices?.options || []}
                        hasNextPage={offices?.hasNextPage}
                        isSearchable={true}
                        onChange={handleFilter}
                        fetchFunction={(handleGetOffice)}
                        labelKeys={['name']}
                        valueKey={'name'}
                    />,
                    sumDisplayed: false,
                    sumBy: '',
                    sumLabel: ''
                },
                {
                    label: 'Profil',
                    value: 'profile',
                    width: 'w-[10%]',
                    sort: true,
                    component: (item: any) => (
                        <div className="flex flex-row items-center justify-center">
                            {getProfileName(item)}
                        </div>
                    ),
                    filterComponent: () => <SelectInput
                        name="profile"
                        options={profiles}
                        hasNextPage={false}
                        isSearchable={true}
                        onChange={handleFilter}
                        labelKeys={['name']}
                        valueKey={'value'}
                    />,
                    sumDisplayed: false,
                    sumBy: '',
                    sumLabel: ''
                },
                {
                    label: 'Statut',
                    value: 'statut',
                    width: 'w-[9%]',
                    sort: true,
                    component: (item: any) => (
                        <div className="flex flex-row items-center justify-center">
                            {item.seniority}
                        </div>
                    ),
                    filterComponent: () => <SelectInput
                        name="seniority"
                        options={seniority?.options || []}
                        hasNextPage={seniority?.hasNextPage}
                        isSearchable={true}
                        onChange={handleFilter}
                        fetchFunction={(handleGetSeniority)}
                        labelKeys={['name']}
                        valueKey={'name'}
                    />,
                    sumDisplayed: false,
                    sumBy: '',
                    sumLabel: ''
                }
            ]
        }
    );
};
